import { Box, Link, Typography } from '@mui/material';
import React from 'react';
import { PLATFORM, SECTION } from 'src/common/constants/constants';
import { dateFormatter } from 'src/common/utils/formatters';
import { ImagesCarousel, LocationTag, ProfileInfo } from 'src/common/components';
import PostInteractions from '../PostInteractions/PostInteractions';
import QuotedPost from '../QuotedPost/QuotedPost';
import moment from 'moment/moment';
import { COLORS, Tooltip } from '@cellebrite/saas-ui-common';
import { getKeywordsFromWatchLists, getPlatformImage } from 'src/common/utils/utils';
import { i18n } from 'src/common/i18n/i18n';
import { GlobalContext } from 'src/common/contexts/globalContext';
import MarkedText from 'src/common/components/MarkedText/MarkedText';
import MultiMarkedText from 'src/common/components/MultiMarkedText/MultiMarkedText';

interface ReadMorePostContentProps {
    post: any;
    onReactionsClick?: any;
    onCommentsClick?: any;
}

const ReadMorePostContent: React.FC<ReadMorePostContentProps> = ({ post, onReactionsClick, onCommentsClick }) => {
    const { reportData } = React.useContext(GlobalContext);
    const getItems = () => {
        const items = post.media.map((mediaPart) => {
            return {
                src: mediaPart.url,
                label: post.location?.address,
                hideLabel: true,
                imageProps: {
                    containerStyles: { height: 260 },
                    roundedCorners: true,
                    contain: true,
                    videoType: mediaPart.type === 'Video',
                    elementInImage: post.location?.address && <LocationTag address={post.location.address} />,
                },
            };
        });
        return items;
    };
    const postKeywords = getKeywordsFromWatchLists(post.watchlist, reportData.currentHighlightsWatchlist?.id);
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <img width="16" height="16" src={getPlatformImage(post.platform)} alt="" />
                {post.title && (
                    <Typography sx={{ ml: 1 }} variant="s14w500">
                        {post.platform ? PLATFORM[post.platform] : post.title}
                    </Typography>
                )}
                {post.titleComment &&
                    (reportData.displayHighlights ? (
                        <MultiMarkedText
                            containerProps={{
                                sx: {
                                    'ml': 1,
                                    'color': COLORS.MUTED_DARK,
                                    '& mark': {
                                        backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                    },
                                },
                            }}
                            searchTextArr={postKeywords}
                        >
                            {post.titleComment}
                        </MultiMarkedText>
                    ) : (
                        <MarkedText
                            containerProps={{
                                sx: {
                                    'ml': 1,
                                    'color': COLORS.MUTED_DARK,
                                    '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                                },
                            }}
                            searchText={reportData.find}
                        >
                            {post.titleComment}
                        </MarkedText>
                    ))}
            </Box>
            {post.headline?.includes(' replied on this') ? (
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                    {reportData.displayHighlights ? (
                        <MultiMarkedText
                            containerProps={{
                                sx: {
                                    'lineHeight': '16px',
                                    'backgroundColor': COLORS.MUTED_SOFT,
                                    '& mark': {
                                        backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                    },
                                },
                            }}
                            searchTextArr={postKeywords}
                        >
                            {post.headline?.split(' replied on this')[0]}
                        </MultiMarkedText>
                    ) : (
                        <MarkedText
                            containerProps={{
                                sx: {
                                    'lineHeight': '16px',
                                    'backgroundColor': COLORS.MUTED_SOFT,
                                    '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                                },
                            }}
                            searchText={reportData.find}
                        >
                            {post.headline?.split(' replied on this')[0]}
                        </MarkedText>
                    )}
                    <Link
                        sx={{ pl: 0.5, pr: 0.5, backgroundColor: COLORS.MUTED_SOFT, lineHeight: '16px' }}
                        onClick={(event) => onCommentsClick?.(event, true)}
                    >
                        {i18n.translate('replied')}
                    </Link>
                    {reportData.displayHighlights ? (
                        <MultiMarkedText
                            containerProps={{
                                sx: {
                                    'lineHeight': '16px',
                                    'backgroundColor': COLORS.MUTED_SOFT,
                                    '& mark': {
                                        backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                    },
                                },
                            }}
                            searchTextArr={postKeywords}
                        >
                            {i18n.translate('on this')}
                        </MultiMarkedText>
                    ) : (
                        <MarkedText
                            containerProps={{
                                sx: {
                                    'lineHeight': '16px',
                                    'backgroundColor': COLORS.MUTED_SOFT,
                                    '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                                },
                            }}
                            searchText={reportData.find}
                        >
                            {i18n.translate('on this')}
                        </MarkedText>
                    )}
                </Box>
            ) : (
                <Box sx={{ mt: post.headline ? 1 : 0, display: 'flex', alignItems: 'center' }}>
                    {reportData.displayHighlights ? (
                        <MultiMarkedText
                            containerProps={{
                                sx: {
                                    'lineHeight': `${post.headline ? 16 : 0}px`,
                                    'backgroundColor': COLORS.MUTED_SOFT,
                                    '& mark': {
                                        backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                    },
                                },
                            }}
                            searchTextArr={postKeywords}
                        >
                            {post.headline}
                        </MultiMarkedText>
                    ) : (
                        <MarkedText
                            containerProps={{
                                sx: {
                                    'lineHeight': `${post.headline ? 16 : 0}px`,
                                    'backgroundColor': COLORS.MUTED_SOFT,
                                    '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                                },
                            }}
                            searchText={reportData.find}
                        >
                            {post.headline}
                        </MarkedText>
                    )}
                </Box>
            )}
            <ProfileInfo
                styles={{ mt: 2 }}
                key={post.title}
                imageSrc={post.profileInfo.imageSrc}
                topInfo={
                    reportData.displayHighlights ? (
                        <MultiMarkedText
                            containerProps={{
                                variant: 's14w500',
                                sx: {
                                    '& mark': {
                                        backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                    },
                                },
                            }}
                            searchTextArr={postKeywords}
                        >
                            {post.profileInfo.name}
                        </MultiMarkedText>
                    ) : (
                        <MarkedText
                            containerProps={{
                                variant: 's14w500',
                                sx: { '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` } },
                            }}
                            searchText={reportData.find}
                        >
                            {post.profileInfo.name}
                        </MarkedText>
                    )
                }
                bottomInfo={
                    reportData.displayHighlights ? (
                        <MultiMarkedText
                            containerProps={{
                                sx: {
                                    '& mark': {
                                        backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                    },
                                },
                            }}
                            searchTextArr={postKeywords}
                        >
                            {post.profileInfo.id}
                        </MultiMarkedText>
                    ) : (
                        <MarkedText
                            containerProps={{
                                sx: { '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` } },
                            }}
                            searchText={reportData.find}
                        >
                            {post.profileInfo.id}
                        </MarkedText>
                    )
                }
            />
            {moment(post.publishDate).isValid() ? (
                <MarkedText
                    containerProps={{
                        sx: {
                            'fontSize': '12px',
                            'mt': 2,
                            'color': COLORS.MUTED_DARK,
                            'display': 'inline-flex',
                            '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                        },
                    }}
                    searchText={reportData.find}
                >
                    {dateFormatter(post.publishDate)}
                </MarkedText>
            ) : (
                <Tooltip
                    content={
                        <Typography>{`Relative to the search date - ${dateFormatter(post.collectionTime)}`}</Typography>
                    }
                >
                    <MarkedText
                        containerProps={{
                            sx: {
                                'fontSize': '12px',
                                'mt': 2,
                                'color': COLORS.MUTED_DARK,
                                'display': 'inline-flex',
                                '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                            },
                        }}
                        searchText={reportData.find}
                    >
                        {dateFormatter(post.publishDate)}
                    </MarkedText>
                </Tooltip>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                {post.bodyTitle &&
                    (reportData.displayHighlights ? (
                        <MultiMarkedText
                            containerProps={{
                                title: post.bodyTitle,
                                sx: {
                                    '& mark': {
                                        backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                    },
                                    'whiteSpace': 'pre-line',
                                    'fontWeight': 500,
                                },
                            }}
                            searchTextArr={postKeywords}
                        >
                            {post.bodyTitle}
                        </MultiMarkedText>
                    ) : (
                        <MarkedText
                            containerProps={{
                                title: post.bodyTitle,
                                sx: {
                                    '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                                    'whiteSpace': 'pre-line',
                                    'fontWeight': 500,
                                },
                            }}
                            searchText={reportData.find}
                        >
                            {post.bodyTitle}
                        </MarkedText>
                    ))}
                {reportData.displayHighlights ? (
                    <MultiMarkedText
                        containerProps={{
                            title: post.body,
                            sx: {
                                '& mark': {
                                    backgroundColor: `${COLORS.NEGATIVE_SOFT} !important`,
                                },
                                'whiteSpace': 'pre-line',
                            },
                        }}
                        searchTextArr={postKeywords}
                    >
                        {post.body}
                    </MultiMarkedText>
                ) : (
                    <MarkedText
                        containerProps={{
                            title: post.body,
                            sx: {
                                '& mark': { backgroundColor: `${COLORS.WARNING_LIGHTEST} !important` },
                                'whiteSpace': 'pre-line',
                            },
                        }}
                        searchText={reportData.find}
                    >
                        {post.body}
                    </MarkedText>
                )}
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                }}
            >
                {!!post.media?.length && <ImagesCarousel items={getItems()} />}
            </Box>
            {!post.media?.length && post.location?.address && <LocationTag address={post.location.address} />}
            <Box
                sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                }}
            >
                {!!post.quotedPosts?.length && (
                    <QuotedPost
                        post={post.quotedPosts[0]}
                        extraImageContainerHeight={134}
                        section={SECTION.POSTS}
                        readMore={true}
                    />
                )}
            </Box>
            <PostInteractions
                post={post}
                onReactionsClick={(event) => onReactionsClick?.(event)}
                onCommentsClick={(event) => onCommentsClick?.(event)}
            />
        </Box>
    );
};

export default ReadMorePostContent;
